import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getSEOData } from '../utils/seoData';
import { TRUST_POINTS, GUARANTEES, CLINICAL_DISCLAIMER } from '../data/ewm';
import './Narrative.css';

function Trust() {
  return (
    <div className="np-page">
      <SEO {...getSEOData('trust')} />

      {/* Hero */}
      <section className="np-hero">
        <div className="container">
          <span className="np-eyebrow">Trust &amp; Security</span>
          <h1>Keep your AI. Add the memory you can prove.</h1>
          <p className="np-hero-sub">
            The side-car is designed for buyers for whom data cannot leave the building and
            generated answers are a liability. The guarantees are properties of the construction,
            not promises in a contract.
          </p>
        </div>
      </section>

      {/* Trust points */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">What you can rely on</h2>
          <p className="np-lead">
            These hold for any host model, at any scale — because they follow from the algebra
            rather than from an implementation choice.
          </p>
          <div className="np-grid np-grid--3">
            {TRUST_POINTS.map((t) => (
              <div key={t.title} className="np-card">
                <div className="np-card-icon">{t.icon}</div>
                <h3 className="np-h3">{t.title}</h3>
                <p className="np-body">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantees recap */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2">The structural guarantees</h2>
          <p className="np-lead">
            Auditability here is a consequence of content-addressing, not of a logging pipeline
            bolted on afterwards.
          </p>
          <div className="np-table-wrap">
            <table className="np-table">
              <thead>
                <tr>
                  <th style={{ width: '230px' }}>Guarantee</th>
                  <th>What it means in practice</th>
                </tr>
              </thead>
              <tbody>
                {GUARANTEES.map((g) => (
                  <tr key={g.name}>
                    <td><strong>{g.name}</strong></td>
                    <td>{g.body}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Data handling */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2">How data is handled</h2>
          <div className="np-grid np-grid--2">
            <div className="np-card">
              <h3 className="np-h3">We never read your prompts or your documents</h3>
              <p className="np-body">
                The side-car reads the numbers the model already produces internally — the
                encodings — not the text. Nothing sensitive is re-exposed, and there is no prompt
                surgery in the loop.
              </p>
            </div>
            <div className="np-card">
              <h3 className="np-h3">History is append-only</h3>
              <p className="np-body">
                Every step forward is a new, timestamped, immutable record. Nothing is overwritten,
                so you can always show what the system knew and when.
              </p>
            </div>
            <div className="np-card">
              <h3 className="np-h3">Recovery is a pop, not a rebuild</h3>
              <p className="np-body">
                State lives outside the processing unit, in a shareable cache backed by a
                content-addressed store. The unit itself owns nothing but a handle.
              </p>
            </div>
            <div className="np-card">
              <h3 className="np-h3">Auditable change detection</h3>
              <p className="np-body">
                D / R / N per commit — departed, retained, novel — with a conservation balance.
                &ldquo;What changed?&rdquo; has a precise, structural answer rather than a summary.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Clinical disclaimer */}
      <section className="np-section np-section--alt">
        <div className="container">
          <h2 className="np-h2">Research use &amp; disclaimers</h2>
          <div className="np-disclaimer" style={{ marginTop: '18px' }}>{CLINICAL_DISCLAIMER}</div>
          <p className="np-body" style={{ marginTop: '18px' }}>
            Clinical and diagnostic features are provided for partner evaluation and research
            demonstration. They are not intended for diagnostic use, do not constitute medical
            advice, and must not be used as the sole basis for any clinical decision.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="np-section">
        <div className="container">
          <h2 className="np-h2 np-h2--center">Questions about deployment or compliance?</h2>
          <p className="np-lead np-lead--center">
            We are happy to walk through the architecture, the storage model and the audit trail
            with your security and compliance team.
          </p>
          <div className="np-cta-row np-cta-row--center">
            <Link to="/contact" className="np-btn np-btn--primary">Contact us</Link>
            <Link to="/technology" className="np-btn np-btn--secondary">Read the architecture</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Trust;
